import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';
import type { DashboardOverview, NewsIntelligence, MarketAnalysis, CommodityPrices, CommodityPrice } from '../services/api';
import ChartModal from './ChartModal';

// Utility functions moved outside component scope
const formatNumber = (num: number | string, decimals = 2) => {
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(numValue)) return 'N/A';
  
  if (Math.abs(numValue) >= 1e9) {
    return `${(numValue / 1e9).toFixed(decimals)}B`;
  } else if (Math.abs(numValue) >= 1e6) {
    return `${(numValue / 1e6).toFixed(decimals)}M`;
  } else if (Math.abs(numValue) >= 1e3) {
    return `${(numValue / 1e3).toFixed(decimals)}K`;
  }
  return numValue.toFixed(decimals);
};

const formatCurrency = (num: number | string) => {
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(numValue)) return 'N/A';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

const getSentimentColor = (sentiment: number) => {
  if (sentiment > 0.1) return 'text-emerald-600';
  if (sentiment < -0.1) return 'text-red-600';
  return 'text-amber-600';
};

const getSentimentIndicator = (sentiment: number) => {
  if (sentiment > 0.1) return '↗';
  if (sentiment < -0.1) return '↘';
  return '→';
};

const getImpactColor = (impact: string) => {
  switch (impact) {
    case 'bullish': case 'favorable': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    case 'bearish': case 'caution': return 'text-red-700 bg-red-50 border-red-200';
    case 'stable': return 'text-blue-700 bg-blue-50 border-blue-200';
    default: return 'text-gray-700 bg-gray-50 border-gray-200';
  }
};

type TimePeriod = 'day' | 'week' | 'month' | 'ytd';

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [selectedIndustry, setSelectedIndustry] = useState<'coal' | 'steel' | 'transportation' | 'energy'>('coal');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('day');
  
  // Chart modal state
  const [chartModal, setChartModal] = useState<{
    isOpen: boolean;
    symbol: string;
    name: string;
    type: 'stock' | 'commodity';
  }>({
    isOpen: false,
    symbol: '',
    name: '',
    type: 'stock'
  });

  // Chart modal functions
  const openChart = (symbol: string, name: string, type: 'stock' | 'commodity') => {
    setChartModal({
      isOpen: true,
      symbol,
      name,
      type
    });
  };

  const closeChart = () => {
    setChartModal({
      isOpen: false,
      symbol: '',
      name: '',
      type: 'stock'
    });
  };

  // Utility function to format large numbers compactly
  const formatIndexValue = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toFixed(2);
  };

  const loadDashboardData = async () => {
    try {
      setRefreshing(true);
      const data = await ApiService.getDashboardOverview();
      setDashboardData(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const formatPercent = (value: number | undefined | null, showSign: boolean = true): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    const formatted = Number(value).toFixed(2);
    const sign = showSign && value > 0 ? '+' : '';
    return `${sign}${formatted}%`;
  };

  const getChangeColor = (value: number | undefined | null): string => {
    if (value === null || value === undefined || isNaN(value)) return 'text-gray-500';
    return value >= 0 ? 'text-emerald-600' : 'text-red-600';
  };

  const getChangeIcon = (value: number | undefined | null): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    return value >= 0 ? '↗' : '↘';
  };

  const getChangeValue = (change: any, period: TimePeriod): number | undefined => {
    if (!change) return undefined;
    switch (period) {
      case 'day': return change.day_change_percent;
      case 'week': return change.week_change_percent;
      case 'month': return change.month_change_percent;
      case 'ytd': return change.ytd_change_percent;
      default: return change.day_change_percent;
    }
  };

  const TimePeriodToggle = () => (
    <div className="flex items-center space-x-2 mb-4">
      <span className="text-sm font-medium text-gray-700">Time Period:</span>
      <div className="flex bg-gray-100 rounded-lg p-1">
        {[
          { key: 'day' as const, label: '1D' },
          { key: 'week' as const, label: '1W' },
          { key: 'month' as const, label: '1M' },
          { key: 'ytd' as const, label: 'YTD' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTimePeriod(key)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              timePeriod === key
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Dashboard</h2>
          <p className="text-gray-600">Analyzing markets, news, and generating insights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md border">
          <div className="text-red-500 text-4xl mb-4">⚠</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Dashboard Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Data Available</h2>
          <p className="text-gray-600">The dashboard is not available at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                The Core Briefing
                <span className="ml-3 text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-md">
                  Executive Dashboard
                </span>
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                AI-Powered Intelligence for Core Natural Resources • Last updated {new Date(dashboardData.timestamp).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={loadDashboardData}
                disabled={refreshing}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md transition-colors flex items-center font-medium"
              >
                {refreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TimePeriodToggle />

        {/* AI Executive Summary */}
        {dashboardData.aiInsights && (
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      AI Executive Summary
                    </h2>
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-md">
                      Confidence: {formatPercent(dashboardData.aiInsights.confidence * 100, false)}
                    </span>
                  </div>
                  <p className="text-gray-700 text-base leading-relaxed mb-6">
                    {dashboardData.aiInsights.executiveSummary}
                  </p>
                </div>
              </div>
              
              {/* Key Insights Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dashboardData.aiInsights.keyInsights.slice(0, 3).map((insight, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${getImpactColor(insight.impact)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">{insight.category}</span>
                      <span className="px-2 py-1 rounded text-xs font-medium bg-white bg-opacity-80">
                        {insight.impact}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed">{insight.insight}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Key Metrics Grid - 6 specific items */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
          {dashboardData.marketAnalysis?.keyMetrics && (
            <>
              {/* CNR */}
              {dashboardData.marketAnalysis.keyMetrics.cnr && (
                <div 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openChart('CNR', 'Core Natural Resources', 'stock')}
                >
                  <MetricCard
                    title="CNR"
                    value={formatCurrency(Number(dashboardData.marketAnalysis.keyMetrics.cnr.close_price))}
                    subtitle="Core Natural Resources"
                    category="equity"
                  />
                </div>
              )}
              
              {/* S&P 500 */}
              {dashboardData.marketAnalysis.keyMetrics.sp500 && (
                <div 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openChart('^GSPC', 'S&P 500', 'stock')}
                >
                  <MetricCard
                    title="S&P 500"
                    value={formatIndexValue(Number(dashboardData.marketAnalysis.keyMetrics.sp500.close_price))}
                    subtitle="US Stock Market Index"
                    category="equity"
                  />
                </div>
              )}
              
              {/* HRC Steel */}
              {dashboardData.marketAnalysis.keyMetrics.hrcSteel && (
                <div 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openChart('HRC=F', 'Hot-Rolled Coil Steel', 'commodity')}
                >
                  <MetricCard
                    title="HRC Steel"
                    value={formatCurrency(Number(dashboardData.marketAnalysis.keyMetrics.hrcSteel.close_price))}
                    subtitle="Hot-Rolled Coil Steel"
                    category="commodity"
                  />
                </div>
              )}
              
              {/* API2 Coal */}
              {dashboardData.marketAnalysis.keyMetrics.api2Coal && (
                <MetricCard
                  title="API2 Coal"
                  value={typeof dashboardData.marketAnalysis.keyMetrics.api2Coal.price === 'string' 
                    ? dashboardData.marketAnalysis.keyMetrics.api2Coal.price 
                    : formatCurrency(Number(dashboardData.marketAnalysis.keyMetrics.api2Coal.price))}
                  subtitle="Coal Price Index"
                  category="commodity"
                />
              )}
              
              {/* Treasury Yield */}
              {dashboardData.marketAnalysis.keyMetrics.treasuryYield && (
                <MetricCard
                  title="Treasury Yield"
                  value={typeof dashboardData.marketAnalysis.keyMetrics.treasuryYield.price === 'string' 
                    ? dashboardData.marketAnalysis.keyMetrics.treasuryYield.price 
                    : `${Number(dashboardData.marketAnalysis.keyMetrics.treasuryYield.price).toFixed(2)}%`}
                  subtitle="US Treasury Yield"
                  category="bond"
                />
              )}
              
              {/* News Articles Count */}
              {dashboardData.marketAnalysis.keyMetrics.newsArticles && (
                <MetricCard
                  title="News Articles"
                  value={dashboardData.marketAnalysis.keyMetrics.newsArticles.count.toString()}
                  subtitle="Last 24 Hours"
                  category="news"
                />
              )}
            </>
          )}
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* News Intelligence Panel */}
          <div className="lg:col-span-1 bg-white rounded-xl p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">News Intelligence</h2>
            
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="text-2xl font-bold text-emerald-600">
                    {dashboardData.newsIntelligence.totalArticles}
                  </div>
                  <div className="text-xs text-gray-600 font-medium">Total Articles</div>
                  <div className="text-xs text-emerald-600 mt-1">Last 24 Hours</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {dashboardData.newsIntelligence.priorityArticles?.filter(a => a.relevanceScore && a.relevanceScore >= 50).length || 0}
                  </div>
                  <div className="text-xs text-gray-600 font-medium">High Priority</div>
                  <div className="text-xs text-blue-600 mt-1">CNR Relevant</div>
                </div>
              </div>
              
              {/* Coal Industry Focus Meter */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-amber-700">Coal Industry Focus</span>
                  <span className="text-xs text-amber-600">
                    {Math.round((dashboardData.newsIntelligence.priorityArticles?.filter(a => 
                      a.title.toLowerCase().includes('coal') || 
                      a.title.toLowerCase().includes('mining') ||
                      a.source === 'coalage' || 
                      a.source === 'coaltrader'
                    ).length || 0) / Math.max(dashboardData.newsIntelligence.priorityArticles?.length || 1, 1) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-amber-200 rounded-full h-2">
                  <div 
                    className="bg-amber-500 h-2 rounded-full transition-all duration-500" 
                    style={{ 
                      width: `${Math.round((dashboardData.newsIntelligence.priorityArticles?.filter(a => 
                        a.title.toLowerCase().includes('coal') || 
                        a.title.toLowerCase().includes('mining') ||
                        a.source === 'coalage' || 
                        a.source === 'coaltrader'
                      ).length || 0) / Math.max(dashboardData.newsIntelligence.priorityArticles?.length || 1, 1) * 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700 uppercase tracking-wide text-sm">
                AI-Ranked News for CNR Executives
              </h3>
              {dashboardData.newsIntelligence.priorityArticles && 
               dashboardData.newsIntelligence.priorityArticles.length > 0 ? (
                dashboardData.newsIntelligence.priorityArticles
                  .filter(article => article && article.title && article.title.trim().length > 0)
                  .slice(0, 10)
                  .map((article, index) => {
                    const relevanceScore = article.relevanceScore || 0;
                    const relevanceLevel = relevanceScore >= 70 ? 'critical' : 
                                         relevanceScore >= 50 ? 'high' : 
                                         relevanceScore >= 30 ? 'medium' : 'low';
                    const borderColor = relevanceLevel === 'critical' ? 'border-red-500' :
                                       relevanceLevel === 'high' ? 'border-orange-500' :
                                       relevanceLevel === 'medium' ? 'border-yellow-500' : 'border-emerald-500';
                    const bgColor = relevanceLevel === 'critical' ? 'bg-red-50' :
                                   relevanceLevel === 'high' ? 'bg-orange-50' :
                                   relevanceLevel === 'medium' ? 'bg-yellow-50' : 'bg-emerald-50';
                    const scoreColor = relevanceLevel === 'critical' ? 'text-red-700 bg-red-100' :
                                      relevanceLevel === 'high' ? 'text-orange-700 bg-orange-100' :
                                      relevanceLevel === 'medium' ? 'text-yellow-700 bg-yellow-100' : 'text-emerald-700 bg-emerald-100';
                    
                    return (
                      <div key={index} className={`border-l-4 ${borderColor} ${bgColor} rounded-r-lg pl-3 pr-3 py-3 hover:shadow-md transition-shadow`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${scoreColor}`}>
                              {relevanceScore.toFixed(0)}
                            </span>
                            <span className="text-xs text-gray-600 font-medium uppercase">
                              {relevanceLevel} priority
                            </span>
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(article.published_at || article.scraped_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h4 className="font-medium text-gray-900 text-sm leading-tight mb-2 cursor-pointer hover:text-blue-600" 
                            onClick={() => window.open(article.url, '_blank')}>
                          {article.title}
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 uppercase font-medium">
                            {article.source || 'Unknown Source'}
                          </span>
                          {relevanceLevel === 'critical' && (
                            <span className="text-xs text-red-600 font-bold">
                              🔥 URGENT
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <div className="text-4xl mb-2">🤖</div>
                  <p className="text-sm font-medium">AI News Analysis Loading...</p>
                  <p className="text-xs text-gray-400 mt-1">Ranking articles by relevance to Core Natural Resources</p>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-700 uppercase tracking-wide text-sm mb-3">
                Key Industry Terms
              </h3>
              <div className="flex flex-wrap gap-2">
                {dashboardData.newsIntelligence.keyTopics?.slice(0, 8).map((topic, index) => {
                  const topicWord = typeof topic === 'string' ? topic : topic.word;
                  const isCoalRelated = ['coal', 'mining', 'metallurgical', 'thermal', 'cnr', 'consol', 'transportation', 'steel'].some(term => 
                    topicWord.toLowerCase().includes(term)
                  );
                  return (
                    <span 
                      key={index} 
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isCoalRelated 
                          ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                          : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      }`}
                    >
                      {isCoalRelated && '⭐ '}{topicWord}
                    </span>
                  );
                }) || (
                  <span className="text-gray-500 text-sm">Analyzing industry topics...</span>
                )}
              </div>
              
              {/* AI Relevance Explanation */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <span className="text-blue-500 text-sm">🤖</span>
                  <div>
                    <p className="text-xs text-blue-700 font-medium">AI Relevance Scoring</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Articles are ranked by relevance to Core Natural Resources using AI analysis of company mentions, 
                      coal industry keywords, competitor activity, and market impact.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Market Analysis Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Natural Resources Stocks */}
            {dashboardData.marketAnalysis.naturalResourcesStocks && 
             dashboardData.marketAnalysis.naturalResourcesStocks.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">Natural Resources Stocks</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dashboardData.marketAnalysis.naturalResourcesStocks.slice(0, 8).map((stock: any, index: number) => {
                    const changeValue = getChangeValue(stock, timePeriod);
                    return (
                      <div 
                        key={index} 
                        className="bg-blue-50 rounded-lg p-4 border border-blue-200 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => openChart(stock.symbol, stock.symbol_name, 'stock')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {stock.symbol.substring(0, 2)}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{stock.symbol}</h4>
                              <p className="text-sm text-gray-600">{stock.symbol_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900">
                              {formatCurrency(Number(stock.close_price))}
                            </div>
                            {changeValue !== null && changeValue !== undefined && typeof changeValue === 'number' && (
                              <div className={`text-sm font-medium ${changeValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {changeValue >= 0 ? '+' : ''}{changeValue.toFixed(2)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Commodities */}
            {dashboardData.marketAnalysis.commodities && 
             dashboardData.marketAnalysis.commodities.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">Commodities</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dashboardData.marketAnalysis.commodities.slice(0, 8).map((commodity: any, index: number) => {
                    const changeValue = getChangeValue(commodity, timePeriod);
                    return (
                      <div 
                        key={index} 
                        className="bg-amber-50 rounded-lg p-4 border border-amber-200 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => openChart(commodity.symbol, commodity.symbol_name, 'commodity')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {commodity.symbol.substring(0, 2)}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{commodity.symbol}</h4>
                              <p className="text-sm text-gray-600">{commodity.symbol_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900">
                              {formatCurrency(Number(commodity.close_price))}
                            </div>
                            {changeValue !== null && changeValue !== undefined && typeof changeValue === 'number' && (
                              <div className={`text-sm font-medium ${changeValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {changeValue >= 0 ? '+' : ''}{changeValue.toFixed(2)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other Important Stocks */}
            {dashboardData.marketAnalysis.otherStocks && 
             dashboardData.marketAnalysis.otherStocks.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">Other Important Stocks</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dashboardData.marketAnalysis.otherStocks.slice(0, 8).map((stock: any, index: number) => {
                    const changeValue = getChangeValue(stock, timePeriod);
                    return (
                      <div 
                        key={index} 
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => openChart(stock.symbol, stock.symbol_name, 'stock')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {stock.symbol.substring(0, 2)}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{stock.symbol}</h4>
                              <p className="text-sm text-gray-600">{stock.symbol_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900">
                              {formatCurrency(Number(stock.close_price))}
                            </div>
                            {changeValue !== null && changeValue !== undefined && typeof changeValue === 'number' && (
                              <div className={`text-sm font-medium ${changeValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {changeValue >= 0 ? '+' : ''}{changeValue.toFixed(2)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* S3 Commodity Data */}
            {dashboardData.commodityPrices && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">Commodity Prices</h2>
                
                {/* Coal Prices */}
                {dashboardData.commodityPrices.categories.coal && 
                 dashboardData.commodityPrices.categories.coal.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-amber-700 uppercase tracking-wide text-sm mb-3">Coal Prices</h3>
                    <div className="space-y-3">
                      {dashboardData.commodityPrices.categories.coal.map((coal, index) => (
                        <div key={index} className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{coal.name}</h4>
                              {coal.status === 'unavailable' ? (
                                <p className="text-sm text-gray-500">Data currently unavailable</p>
                              ) : (
                                <div className="flex items-center space-x-4 mt-1">
                                  {coal.previous && typeof coal.latest === 'number' && typeof coal.previous === 'number' && (
                                    <span className="text-xs text-gray-600">
                                      Prev: {formatNumber(coal.previous, 2)}
                                    </span>
                                  )}
                                  {coal.daily_change && (
                                    <span className={`text-xs font-medium ${getChangeColor(coal.daily_change)}`}>
                                      {getChangeIcon(coal.daily_change)} {formatPercent(coal.daily_change)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                {typeof coal.latest === 'number' ? formatNumber(coal.latest, 2) : coal.latest}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Steel Prices */}
                {dashboardData.commodityPrices.categories.steel && 
                 dashboardData.commodityPrices.categories.steel.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-blue-700 uppercase tracking-wide text-sm mb-3">Steel Prices</h3>
                    <div className="space-y-3">
                      {dashboardData.commodityPrices.categories.steel.map((steel, index) => (
                        <div key={index} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{steel.name}</h4>
                              {steel.status === 'unavailable' ? (
                                <p className="text-sm text-gray-500">Data currently unavailable</p>
                              ) : (
                                <div className="flex items-center space-x-4 mt-1">
                                  {steel.previous && typeof steel.latest === 'number' && typeof steel.previous === 'number' && (
                                    <span className="text-xs text-gray-600">
                                      Prev: {formatNumber(steel.previous, 2)}
                                    </span>
                                  )}
                                  {steel.weekly_change && (
                                    <span className={`text-xs font-medium ${getChangeColor(steel.weekly_change)}`}>
                                      {getChangeIcon(steel.weekly_change)} {formatPercent(steel.weekly_change)} (7d)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                {typeof steel.latest === 'number' ? formatNumber(steel.latest, 2) : steel.latest}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Treasury Yields */}
                {dashboardData.commodityPrices.categories.yield && 
                 dashboardData.commodityPrices.categories.yield.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-emerald-700 uppercase tracking-wide text-sm mb-3">Treasury Yields</h3>
                    <div className="space-y-3">
                      {dashboardData.commodityPrices.categories.yield.map((yield_, index) => (
                        <div key={index} className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{yield_.name}</h4>
                              {yield_.status === 'unavailable' ? (
                                <p className="text-sm text-gray-500">Data currently unavailable</p>
                              ) : (
                                <div className="flex items-center space-x-4 mt-1">
                                  {yield_.previous && typeof yield_.latest === 'number' && typeof yield_.previous === 'number' && (
                                    <span className="text-xs text-gray-600">
                                      Prev: {formatNumber(yield_.previous, 2)}%
                                    </span>
                                  )}
                                  {yield_.daily_change && (
                                    <span className={`text-xs font-medium ${getChangeColor(yield_.daily_change)}`}>
                                      {getChangeIcon(yield_.daily_change)} {formatPercent(yield_.daily_change)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                {typeof yield_.latest === 'number' ? `${formatNumber(yield_.latest, 2)}%` : yield_.latest}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bond Information */}
                {dashboardData.commodityPrices.categories.bonds && 
                 dashboardData.commodityPrices.categories.bonds.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-purple-700 uppercase tracking-wide text-sm mb-3">Bond Information</h3>
                    <div className="space-y-3">
                      {dashboardData.commodityPrices.categories.bonds.map((bond, index) => (
                        <div key={index} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h4 className="font-medium text-gray-900">{bond.name}</h4>
                                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded font-mono">
                                  {bond.id}
                                </span>
                              </div>
                              {bond.status === 'unavailable' ? (
                                <p className="text-sm text-gray-500">Bond data currently unavailable</p>
                              ) : (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                                  {bond.coupon && bond.coupon !== 'N/A' && (
                                    <div className="text-xs">
                                      <span className="text-gray-500">Coupon:</span> <span className="font-medium">{bond.coupon}</span>
                                    </div>
                                  )}
                                  {bond.maturity_date && bond.maturity_date !== 'N/A' && (
                                    <div className="text-xs">
                                      <span className="text-gray-500">Maturity:</span> <span className="font-medium">{bond.maturity_date}</span>
                                    </div>
                                  )}
                                  {bond.principal_amount && bond.principal_amount !== 'N/A' && (
                                    <div className="text-xs">
                                      <span className="text-gray-500">Principal:</span> <span className="font-medium">{bond.principal_amount}</span>
                                    </div>
                                  )}
                                  {bond.latest_trade_date && bond.latest_trade_date !== 'N/A' && (
                                    <div className="text-xs">
                                      <span className="text-gray-500">Last Trade:</span> <span className="font-medium">{bond.latest_trade_date}</span>
                                    </div>
                                  )}
                                  {bond.latest_trade_count && bond.latest_trade_count !== 'N/A' && (
                                    <div className="text-xs">
                                      <span className="text-gray-500">Trade Count:</span> <span className="font-medium">{bond.latest_trade_count}</span>
                                    </div>
                                  )}
                                  {bond.latest_trade_amount && bond.latest_trade_amount !== 'N/A' && (
                                    <div className="text-xs">
                                      <span className="text-gray-500">Trade Amount:</span> <span className="font-medium">{bond.latest_trade_amount}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-lg font-bold text-gray-900">
                                {bond.latest_price_range || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500">Price Range</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Economic Indicators */}
            {dashboardData.economicIndicators && 
             dashboardData.economicIndicators.indicators && 
             dashboardData.economicIndicators.indicators.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">Economic Indicators</h2>
                <div className="space-y-4">
                  {dashboardData.economicIndicators.indicators.slice(0, 6).map((indicator: any, index: number) => (
                    <div key={index} className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{indicator.series_title}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-600 uppercase bg-gray-100 px-2 py-1 rounded">
                              {indicator.category}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(indicator.current_date || indicator.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">
                            {formatNumber(indicator.current_value || indicator.value, 2)}
                            {indicator.units && (
                              <span className="text-sm font-normal text-gray-600 ml-1">
                                {indicator.units}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Chart Modal */}
      <ChartModal
        isOpen={chartModal.isOpen}
        onClose={closeChart}
        symbol={chartModal.symbol}
        name={chartModal.name}
        type={chartModal.type}
      />
    </div>
  );
};

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  category: 'equity' | 'volatility' | 'commodity' | 'news' | 'bond';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, category }) => {
  const getCategoryStyles = (cat: string) => {
    switch (cat) {
      case 'equity': return 'border-blue-200 bg-blue-50';
      case 'volatility': return 'border-red-200 bg-red-50';
      case 'commodity': return 'border-amber-200 bg-amber-50';
      case 'news': return 'border-purple-200 bg-purple-50';
      case 'bond': return 'border-emerald-200 bg-emerald-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-6 min-h-[120px] ${getCategoryStyles(category)}`}>
      <div className="flex items-center justify-between h-full">
        <div className="flex-1 flex flex-col justify-center">
          <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">{title}</h3>
          <div className="mt-2">
            <span className="text-2xl font-bold text-gray-900">{value}</span>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 